import { useEffect } from 'react';
import { useQuery } from 'react-query';
import isEqual from 'lodash/isEqual';
import { auth, useAuthState } from 'api/services/firebase';
import { getBalance, getPayments, getUsage } from 'api/services/payments';
import usePaymentsStore from 'store/paymentStore';
import useAccountStore from 'store/accountStore';
import { sortChartDataKeys } from 'utils/chart';
import { shortDateFormatter } from 'utils/formatDateTime';

export const useGetPayments = () => {
  const [user] = useAuthState(auth);
  const teamId = useAccountStore((state) => state.activeAccount.teamId);

  const { data, isLoading, error } = useQuery(
    ['payments', teamId],
    getPayments(user),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      enabled: !!user,
    },
  );

  const paymentsData = data?.data.map((payment) => ({
    ...payment,
    amount: `$${payment.amount / 100}`,
    date: shortDateFormatter(payment.createdOn),
  }));

  return {
    data: paymentsData,
    totalCount: data?.totalCount,
    isLoading: user ? isLoading : true,
    error,
  };
};

export const useGetBalance = () => {
  const [user] = useAuthState(auth);
  const teamId = useAccountStore((state) => state.activeAccount.teamId);

  const {
    data = null, isLoading, error, refetch, isRefetching,
  } = useQuery(
    ['balance', teamId],
    getBalance(user),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      enabled: !!user,
    },
  );

  const setBalance = usePaymentsStore((state) => state.setBalance);

  useEffect(() => {
    const balanceData = data ? {
      total: data.balance ? data.balance / 100 : 0,
      credit: data.promoAvailable ? data.promoAvailable / 100 : 0,
      hasPromo: data.hasPromo,
      promoUsage: data.promoUsage ? data.promoUsage / 100 : 0,
      promoExpirationDate: data.promoExpirationDate
        ? new Date(data.promoExpirationDate).toLocaleDateString()
        : null,
      isLoading,
      isRefetching,
      error,
    } : undefined;

    setBalance(balanceData);
  }, [data, isLoading, isRefetching, error, setBalance]);

  return {
    refetch,
  };
};

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const usageChartColumnsCount = 6;

export const useGetUsage = () => {
  const [user] = useAuthState(auth);
  const teamId = useAccountStore((state) => state.activeAccount.teamId);

  const setUsage = usePaymentsStore((state) => state.setUsage);
  const chart = usePaymentsStore((state) => state.usage.chart);

  const { data, isLoading, error } = useQuery(
    ['usage', teamId],
    getUsage(),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      enabled: !!user,
    },
  );

  const usageData = data?.data || {};
  const usageDataKeys = sortChartDataKeys(Object.keys(usageData));

  const totalUsage = usageDataKeys.reduce((totalUsageData, month) => {
    // eslint-disable-next-line no-param-reassign
    totalUsageData += Number(usageData[month].costs);
    return totalUsageData;
  }, 0);

  const usageDataKeysStartIndex = usageDataKeys.length > usageChartColumnsCount
    ? usageDataKeys.length - usageChartColumnsCount
    : 0;
  const usageDataKeysOfLastSixMonths = usageDataKeys.slice(usageDataKeysStartIndex);

  const usageDataOfLastSixMonths = usageDataKeysOfLastSixMonths.map((key) => ({
    value: usageData[key].costs / 100,
    month: monthLabels[Number(key.split('_')[1]) - 1],
  }));

  useEffect(() => {
    if (!isEqual(usageDataOfLastSixMonths, chart)) {
      setUsage({
        chart: usageDataOfLastSixMonths,
        totalUsage: totalUsage / 100,
        isLoading,
        error,
      });
    }
  }, [usageDataOfLastSixMonths, isLoading, totalUsage, error, setUsage, chart]);
};
